import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Text, UnstyledButton } from '@mantine/core';
import ArtThumbnail from './ArtThumbnail.js';
import axiosInstance from './../axiosInstance.js';

// Book search results as scannable rows: cover, title, author, the
// reader rating with its vote count, a "+N editions" expander for the
// other editions of the same work (the backend folds them into
// `editions`), and a one-tap "plan" action. Rows, not a poster grid,
// because a search hit is compared by its text, not by its cover.

const THUMB = { width: 56, height: 84 };
const THUMB_SMALL = { width: 36, height: 54 };

const T = {
  ru: {
    plan: 'В планы', planned: 'В планах', finished: 'Прочитано', inProgress: 'Читаю',
    liked: 'Понравилось', editions: (n) => `${n} ${n === 1 ? 'издание' : n < 5 ? 'издания' : 'изданий'}`,
    loose: 'Похожие по названию', readers: 'оценок',
  },
  en: {
    plan: 'Plan', planned: 'Planned', finished: 'Read', inProgress: 'Reading',
    liked: 'Liked', editions: (n) => `${n} edition${n === 1 ? '' : 's'}`,
    loose: 'Loose matches', readers: 'ratings',
  },
};

const compactCount = (n) => {
  if (!n || n < 0) return null;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '')}K`;
  return String(n);
};

const titleOf = (r, language) =>
  (language === 'en' && r.enName) ? r.enName : (r.ruName || r.enName);

// The reader rating worth showing on a search row: LiveLib for Russian
// readers, Goodreads otherwise. Apple/Litres numbers are not trusted
// anywhere in the app, so they are skipped here too.
const readerRating = (art) => {
  const infos = (art && art.otherPlatformInfos) || [];
  const pick = infos.find(i => i.name === 'livelib' && i.score) || infos.find(i => i.name === 'goodreads' && i.score);
  if (!pick) return null;
  return { icon: pick.name === 'livelib' ? '/livelib_icon.png' : '/goodreads_icon.png', score: pick.score, votes: pick.votes, link: pick.link };
};

const Rating = ({ art, language }) => {
  const r = readerRating(art);
  if (!r) return null;
  const votes = compactCount(r.votes);
  return (
    <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--mantine-color-dimmed)' }}>
      <img src={r.icon} alt="" className="rounded-sm" style={{ width: 14, height: 14, opacity: 0.95 }} />
      <span className="font-medium" style={{ color: 'var(--mantine-color-text)' }}>{r.score.toFixed(1)}</span>
      {votes && <span>· {votes} {T[language].readers}</span>}
    </span>
  );
};

const STATUS_LABEL = {
  todo: 'planned', inProgress: 'inProgress', finished: 'finished',
  like: 'liked', veryGood: 'liked', good: 'liked',
};

const PlanButton = ({ result, language, myUser }) => {
  const [tag, setTag] = useState((result.sourceArt && result.sourceArt.myEventTagName) || (result.sourceArt && result.sourceArt.myEventIsFinished ? 'finished' : null));
  const [busy, setBusy] = useState(false);
  if (!myUser) return null;
  const t = T[language];
  if (tag) {
    const key = STATUS_LABEL[tag] || 'planned';
    return (
      <Badge variant="light" color={tag === 'todo' ? 'blue' : 'green'} size="sm" radius="xl" style={{ textTransform: 'none', flexShrink: 0 }}>
        {t[key]}
      </Badge>
    );
  }
  const plan = (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const body = result.sourceArt
      ? { artId: result.sourceArt.id, globalManualTag: 'todo', type: 'like' }
      : { wikiArticleLink: result.pageUrl, globalManualTag: 'todo', type: 'like' };
    axiosInstance.post('/artEvents', body)
      .then(() => { setTag('todo'); if (result.sourceArt) result.sourceArt.myEventTagName = 'todo'; })
      .catch(console.error)
      .finally(() => setBusy(false));
  };
  return (
    <UnstyledButton
      onClick={plan}
      className="rounded-xl text-xs font-medium transition-colors"
      style={{
        flexShrink: 0, padding: '5px 10px', whiteSpace: 'nowrap',
        background: 'var(--mantine-color-default)', color: 'var(--mantine-color-blue-text)',
        opacity: busy ? 0.6 : 1,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default)'; }}
    >
      + {t.plan}
    </UnstyledButton>
  );
};

const linkTo = (r) => (r.sourceArt ? `/arts/${r.sourceArt.id}` : `/artByLink?link=${r.pageUrl}`);

const EditionRow = ({ r, language }) => (
  <li className="flex items-center gap-2 py-1">
    <div className="flex-shrink-0" style={THUMB_SMALL}>
      <ArtThumbnail art={r.sourceArt ? r.sourceArt : { thumbnailUrl: r.thumbUrl || r.imageUrl }} box={THUMB_SMALL} badgeSize={16} />
    </div>
    <div className="flex-1 min-w-0">
      <Link to={linkTo(r)} className="block truncate text-sm hover:underline">{titleOf(r, language)}</Link>
      <div className="flex items-center gap-2 min-w-0">
        {r.author && <Text size="xs" c="dimmed" className="truncate">{r.author}</Text>}
        {r.sourceArt && <Rating art={r.sourceArt} language={language} />}
      </div>
    </div>
  </li>
);

const BookRow = ({ r, language, myUser }) => {
  const [open, setOpen] = useState(false);
  const editions = r.editions || [];
  const meta = [r.author, r.year && r.year !== 9999 ? String(r.year) : null].filter(Boolean).join(' · ');
  return (
    <li className="py-2">
      <div className="flex items-start gap-3">
        <Link to={linkTo(r)} className="flex-shrink-0" style={THUMB}>
          <ArtThumbnail art={r.sourceArt ? r.sourceArt : { thumbnailUrl: r.thumbUrl || r.imageUrl }} box={THUMB} badgeSize={20} />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <Link to={linkTo(r)} className="font-medium leading-snug hover:underline" style={{ fontSize: 15, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {titleOf(r, language)}
            </Link>
            <div className="ml-auto"><PlanButton result={r} language={language} myUser={myUser} /></div>
          </div>
          {meta && <Text size="sm" c="dimmed" className="truncate" mt={2}>{meta}</Text>}
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {r.sourceArt && <Rating art={r.sourceArt} language={language} />}
            {editions.length > 0 && (
              <UnstyledButton
                onClick={(e) => { e.preventDefault(); setOpen(o => !o); }}
                className="text-xs"
                style={{ color: 'var(--mantine-color-blue-text)' }}
              >
                {open ? '▾' : '▸'} {T[language].editions(editions.length + 1)}
              </UnstyledButton>
            )}
          </div>
          {open && editions.length > 0 && (
            <ul className="mt-1 pl-1" style={{ borderLeft: '2px solid var(--mantine-color-default-border)' }}>
              {editions.map(e => <EditionRow key={e.pageUrl} r={e} language={language} />)}
            </ul>
          )}
        </div>
      </div>
    </li>
  );
};

const BookSearchResults = ({ results, language, myUser }) => {
  if (!results) return null;
  const lang = language === 'en' ? 'en' : 'ru';
  const looseAt = results.findIndex(r => r.match === 'words' || r.match === 'fuzzy');
  const showDivider = looseAt > 0; // only when there is something strong above it
  return (
    <ul className="w-full mx-auto px-3 sm:px-0" style={{ maxWidth: 640 }}>
      {results.map((r, i) => (
        <React.Fragment key={r.pageUrl || i}>
          {showDivider && i === looseAt && (
            <li className="flex items-center gap-3 pt-3 pb-1">
              <div className="flex-1" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }} />
              <Text size="xs" c="dimmed">{T[lang].loose}</Text>
              <div className="flex-1" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }} />
            </li>
          )}
          <BookRow r={r} language={lang} myUser={myUser} />
        </React.Fragment>
      ))}
    </ul>
  );
};

export default BookSearchResults;
